import { createThunkRequest, DELETE, GET, POST } from '@webapp/account/src/libs/request';
import { mapCondition } from '@webapp/account/src/resources/logic-link-params/map';

import {
    initialLogicSubCondition,
    type LogicCondition,
    type LogicSubCondition,
    LogicSubConditonType,
    type LogicVariant,
} from '@webapp/common/entities/logic';
import { deepMapQuestion } from '@webapp/common/lib/map';
import { reorderQuestions } from '@webapp/common/resources/question-types';
import { LogicBoolFunc, LogicType } from '@webapp/common/resources/survey';

import { mapPage } from '../common';

import { prepareVariantsParams } from './lib';
import { mapQuestion } from './map';
import { reverseMapCondition, reverseMapConditionNew } from './map-logic';
import type { LogicPageQuestion } from './question';
import { selectExtendedLogic, selectQuestionById } from './selectors';

export const addLogicCondition = createThunkRequest(
    'logic/addCondition',
    async (
        {
            pageId,
            params,
            questionId,
            surveyId,
        }: {
            pageId?: number;
            params: Partial<LogicCondition>;
            questionId?: number;
            surveyId: number;
        },
        { dispatch },
    ) => {
        const { id, message } = await POST('add/logic', {
            surveyId: surveyId,
            questionId: questionId,
            ...reverseMapCondition(params),
        });

        if (!questionId) {
            dispatch(loadSurveyLogic({ surveyId }));
        }
        return {
            surveyId,
            id: Number(id),
            params,
            pageId,
            questionId,
            message,
        };
    },
);

export const addLogicSubCondition = createThunkRequest(
    'logic/addSubCondition',
    async (
        {
            params,
            logicId,
        }: {
            params: Partial<LogicSubCondition>;
            logicId: number;
        },
        { getState, dispatch },
    ) => {
        const { id, message } = await POST('add/logic_condition', {
            logicId,
            ...reverseMapConditionNew(params),
        });

        if (params.type === LogicSubConditonType.LINK_PARAM) {
            const state = getState();
            const extendedLogic = selectExtendedLogic(state);
            const logic = extendedLogic.find((l) => l.id === logicId);
            const lastCondition = logic.conditions[logic.conditions.length - 1];
            if (lastCondition && lastCondition.boolFuncExternal !== LogicBoolFunc.AND) {
                dispatch(
                    changeLogicSubCondition({
                        logicId: lastCondition.id,
                        params: {
                            boolFuncExternal: LogicBoolFunc.AND,
                        },
                    }),
                );
            }
        }

        return {
            logicId,
            id: Number(id),
            params,
            message,
        };
    },
);

export const changeLogicSubCondition = createThunkRequest(
    'logic/changeLogicSubCondition',
    async ({
        params,
        logicId,
    }: {
        params: Omit<LogicSubCondition, 'variants'> & { variants: Array<Pick<LogicVariant, 'id' | 'checked'>> };
        logicId: number;
    }) => {
        if (params.questionId) {
            params.variants = [];
        }
        if (params.type === LogicSubConditonType.LINK_PARAM) {
            delete params.questionId;
        }
        const { id, message } = await POST('change/logic_condition', {
            id: logicId,
            ...reverseMapConditionNew(params),
        });

        return {
            logicId,
            id: Number(id),
            params,
            message,
        };
    },
);

export const removeLogicSubCondition = createThunkRequest(
    'logic/removeLogicSubCondition',
    async ({ id, logicId }: { id: number; logicId: number }) => {
        await DELETE(`delete/logic_condition/${id}`);

        return {
            id,
            logicId,
        };
    },
);

export const removeLogicCondition = createThunkRequest(
    'logic/removeCondition',
    async ({ conditionId, pageId, questionId }: { conditionId: number; pageId: number; questionId: number }) => {
        const { message } = await POST('delete/logic', {
            id: conditionId,
        });

        return { conditionId, pageId, questionId, message };
    },
);

export const changeLogicVariant = createThunkRequest(
    'logic/changeVariant',
    async (
        {
            checked,
            conditionId,
            pageId,
            questionId,
            variantId,
        }: {
            checked: boolean;
            conditionId: number;
            pageId: number;
            questionId: number;
            variantId: number;
        },
        { getState },
    ) => {
        const state = getState();
        const question = selectQuestionById(questionId)(state);
        const condition = question.conditions.find(({ id }) => id === conditionId);

        const { message } = await POST('change/logic', {
            logicId: conditionId,
            params: prepareVariantsParams(condition.variants),
        });

        return { message, conditionId, pageId, questionId, variantId, checked };
    },
);

export const resetLogicVariants = createThunkRequest(
    'logic/resetVariants',
    async ({
        conditionId,
        pageId,
        questionId,
        newVariants,
    }: {
        conditionId: number;
        pageId: number;
        questionId: number;
        newVariants: Array<LogicVariant>;
    }) => {
        const { message } = await POST('change/logic', {
            logicId: conditionId,
            params: prepareVariantsParams(newVariants),
        });

        return { message, conditionId, pageId, questionId, variants: [] };
    },
);

export const changeLogicQuestionId = createThunkRequest(
    'logic/changeQuestion',
    async ({
        conditionId,
        pageId,
        questionId,
        oldQuestionId,
    }: {
        conditionId: number;
        pageId?: number;
        questionId: number;
        oldQuestionId: number;
    }) => {
        const { message } = await POST('change/logic', {
            logicId: conditionId,
            question_id: questionId,
        });

        return { message, conditionId, pageId, oldQuestionId, questionId };
    },
);

export const changeLogicConditionParam = createThunkRequest(
    'logic/changeConditionParam',
    async ({
        conditionId,
        pageId,
        questionId,
        params,
    }: {
        conditionId: number;
        pageId: number;
        questionId: number;
        params: Partial<LogicCondition>;
    }) => {
        const { message } = await POST('change/logic', {
            logicId: conditionId,
            ...reverseMapCondition(params),
        });

        return { message, conditionId, params, pageId, questionId };
    },
);

export const changeExtendedLogicConditionParam = createThunkRequest(
    'logic/changeExtendedLogicConditionParam',
    async ({ conditionId, params }: { conditionId: number; params: Partial<LogicCondition> }) => {
        const { message } = await POST('change/logic', {
            logicId: conditionId,
            ...reverseMapCondition(params),
        });

        return { message, conditionId, params };
    },
);

// TODO remove, use survey state
export const loadLogicData = createThunkRequest(
    'logic/loadData',
    async ({ surveyId }: { surveyId: number }) => {
        const { pages, questions } = await POST('list/page_question', {
            survey: surveyId,
        });
        let questionsMapped: Array<LogicPageQuestion> = reorderQuestions(questions, pages).map(mapQuestion);
        questionsMapped = questionsMapped.map((question) => deepMapQuestion(question, questionsMapped));

        return {
            pages: pages.map(mapPage),
            questions: questionsMapped,
        };
    },
    { notify: false },
);

export const loadSurveyLogic = createThunkRequest(
    'logic/loadSurveyLogic',
    async ({ surveyId }: { surveyId: number }) => {
        const { logics } = await GET('get/survey_logics', {
            surveyId,
            logicType: [LogicType.TYPE_RESPONSE_QUOTA],
        });

        return {
            logics: logics.map(mapCondition),
        };
    },
    { notify: false },
);

export const loadExtendedLogic = createThunkRequest(
    'logic/loadExtendedLogic',
    async ({ surveyId }: { surveyId: number }) => {
        const { logics } = await GET('get/survey_logics', {
            surveyId,
            isExtended: true,
        });

        return {
            logics: logics.map(mapCondition),
        };
    },
    { notify: false },
);

export const removeExtendedLogic = createThunkRequest(
    'logic/removeExtendedLogic',
    async ({ conditionId }: { conditionId: number }) => {
        const { message } = await POST('delete/logic', {
            id: conditionId,
        });

        return { conditionId, message };
    },
);

export const addExtendedLogic = createThunkRequest(
    'logic/addExtendedLogic',
    async ({
        params,
        surveyId,
        firstQuestionId,
    }: {
        params: Partial<LogicCondition>;
        surveyId: number;
        firstQuestionId?: number;
    }) => {
        const { id, message } = await POST('add/logic', {
            surveyId,
            ...reverseMapCondition(params),
        });
        let subcond: LogicSubCondition | undefined = undefined;

        if (firstQuestionId) {
            const subcondParams = {
                ...initialLogicSubCondition,
                questionId: firstQuestionId,
                boolFuncExternal: LogicBoolFunc.AND,
            };
            const { id: subConditionId } = await POST('add/logic_condition', {
                logicId: id,
                ...reverseMapConditionNew(subcondParams),
            });
            subcond = {
                ...subcondParams,
                id: subConditionId,
            };
        }

        return {
            surveyId,
            firstQuestionId,
            id: Number(id),
            params,
            message,
            subcond,
        };
    },
);
